import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../store/store";
import { fetchStepperData, getPortalDataFhpl } from "../../../../models/preAuthSlice";
import {
  approveFhplPreAuth,
  buildFhplApprovePayload,
  buildFhplPreAuthPayload,
  FHPL_DATE_FORMAT,
  getFhplPrefillFromBasicDetails,
  initiateCasePreAuthFhpl,
  intimateFhplPreAuth,
  mapFhplPreAuthOthersToForm,
  saveDraftFhpl,
  updateDraftFhpl,
} from "../../../../apis/SchemeService/FHPL/fhplService";
import PreAuthFhpl from "../../../../components/patient/PreAuth/FHPL/PreAuthFhpl";
import { getBasicDetails, getDeficiencyQuestionData, getTimelineDetails } from "../../../../models/patientSlice";
import { Form, message } from "antd";
import dayjs from "dayjs";
import { errorMessage, successMessage } from "../../../../utils/notificationUtils";
import {
  deleteUploadedDocument,
  getAllStarhealthDocumentsData,
  updateAllDocumentsData,
} from "../../../../apis/patientService";
import { UploadDocument } from "../../../../models/EnhancementSlice";
import FilePreviewModal from "../../../../components/patient/PreAuth-query/FilePreviewModal";
import MultipleFilesPreviewModal from "../../../../components/patient/PreAuth-query/MultipleFilesPreviewModal";
import PatientDetailsStageDocument from "../../../../components/modals/PatientDetailsStageDocument";
import DocumentDeficiencyModal from "../../../../components/patient/DocumentDeficiencyModal";
import ConfirmSubmissionModalStage from "../../../../components/modals/ConfirmSubmissionModalStage";
import ManuallyClaimIdPortal from "../../../../components/patient/PreAuth/ManuallyClaimIdPortal";
import PortalClaimValidationModal from "../../../../components/patient/Portal Registration/PortalClaimValidationModal";
import {
  handlePortalClaimValidationFlow,
  updatePortalClaimValidation,
} from "../../../../utils/portalClaimValidation";

type PreAuthFhplContainerProps = {
  activeKey: any;
  sseData?: any;
  sseUpdateCount?: any;
};

const MANDATORY_FIELDS_ERROR = "Please, fill all mandatory fields";

const parseFhplDate = (value?: string | null) => {
  if (!value) return null;
  const parsed = dayjs(value, [
    FHPL_DATE_FORMAT,
    "DD-MM-YYYY",
    "DD/MM/YYYY HH:mm:ss",
    "DD-MM-YYYY HH:mm:ss",
  ]);
  return parsed.isValid() ? parsed : dayjs(value);
};

const formatFhplDate = (value: any) =>
  value ? dayjs(value).format(FHPL_DATE_FORMAT) : null;

const PreAuthFhplContainer: FC<PreAuthFhplContainerProps> = ({
  activeKey,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm();
  const { patients, preAuth, auth, enhancement } = useSelector((state: RootState) => state);
  const [isSaving, setIsSaving] = useState(false);
  const [isIntimating, setIsIntimating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [manuallyClaimIdPortal, setManuallyClaimIdPortal] = useState(false);
  const [documentTypes, setDocumentTypes] = useState<any[]>([]);
  const [documentRequestResponseDocuments, setDocumentRequestResponseDocuments] = useState<any>();
  const [documentUrls, setDocumentUrls] = useState<any[]>([]);
  const [previewDocument, setPreviewDocument] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [deficiencyModal, setDeficiencyModal] = useState(false);
  const [documentName, setDocumentName] = useState("");
  const [clickedActiveTab, setClickedActiveTab] = useState<any>();
  const [clickedActiveDocumentUrl, setClickedActiveDocumentUrl] = useState<any>();
  const [fetchingDeficiencyData, setFetchingDeficiencyData] = useState(false);

  const [selectedFile, setSelectedFile] = useState<{ file: File; documentType: string } | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<{ file: File; documentType: string }[]>([]);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState({
    url: "",
    name: "",
    showUpload: false,
    documentType: "",
  });
  const [previewFiles, setPreviewFiles] = useState<any[]>([]);
  const [uploadFileList, setUploadFileList] = useState<any[]>([]);
  const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
  const [confirmationState, setConfirmationState] = useState<string>("");
  const [confirmationTitle, setConfirmationTitle] = useState<string>("");
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [validationRes, setValidationRes] = useState<any>(null);
  const [isValidationLoading, setIsValidationLoading] = useState(false);
  const [registrationValidationStatus, setRegistrationValidationStatus] =
    useState<any>(null);

  const fhplPortalData =
    preAuth?.portalData?.casePreAuthOthersResponse?.data ||
    preAuth?.portalData?.casePreAuthFhpl?.data ||
    preAuth?.portalData?.casePreAuthOthersResponse ||
    preAuth?.portalData?.casePreAuthFhpl;

  const isPreAuthTabActive = () => {
    if (!activeKey || activeKey.length === 0) return false;

    const currentStageId =
      typeof activeKey === "string"
        ? parseInt(activeKey)
        : parseInt(activeKey?.[0]);

    const currentStage = preAuth?.stepperData?.visitStages?.find(
      (stage: any) => stage?.visitStageId === currentStageId,
    );

    return currentStage?.visitStage?.toUpperCase() === "PREAUTH";
  };

  const fetchStepperForStatus = () => {
    if (patients?.fetchBasicDetails?.casesResponse?.id) {
      const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
      const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
      dispatch(fetchStepperData({ caseId, visitId }));
    }
  };

  const fetchPreAuthData = async () => {
    try {
      const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
      const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
      if (!caseId || !visitId) return;

      await dispatch(getPortalDataFhpl({ caseId, visitId }));
    } catch (err: any) {
      errorMessage(err?.data?.message || "Failed to fetch FHPL PreAuth data");
    }
  };

  const fetchDocuments = async () => {
    const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
    const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
    const schemeMetaDataId = patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId;
    if (!caseId || !visitId || !schemeMetaDataId) return;

    try {
      const allDocumentList = await getAllStarhealthDocumentsData(caseId, visitId, schemeMetaDataId);
      setDocumentTypes(allDocumentList?.["PreAuth"] || []);
      setDocumentUrls(
        (allDocumentList?.["PreAuth"] || [])
          .filter((item: any) => item.documentList && item.documentList.length > 0)
          .flatMap((item: any) =>
            item.documentList.map((doc: any) => ({
              ...doc,
              documentLabelType: doc.documentLabelType,
            }))
          )
      );
    } catch (err: any) {
      errorMessage(err?.message || "Failed to fetch FHPL documents");
    }
  };

  useEffect(() => {
    if (
      patients?.fetchBasicDetails?.casesResponse?.id &&
      patients?.fetchBasicDetails?.visitInfoResponse?.id &&
      activeKey &&
      activeKey?.length > 0 &&
      isPreAuthTabActive()
    ) {
      fetchPreAuthData();
    }
  }, [
    patients?.fetchBasicDetails?.casesResponse?.id,
    patients?.fetchBasicDetails?.visitInfoResponse?.id,
    activeKey,
    preAuth?.stepperData,
  ]);

  useEffect(() => {
    if (
      patients?.fetchBasicDetails?.casesResponse?.id &&
      patients?.fetchBasicDetails?.visitInfoResponse?.id &&
      activeKey &&
      activeKey?.length > 0 &&
      isPreAuthTabActive()
    ) {
      fetchDocuments();
    }
  }, [
    patients?.fetchBasicDetails?.casesResponse?.id,
    patients?.fetchBasicDetails?.visitInfoResponse?.id,
    patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
    activeKey,
    preAuth?.stepperData,
  ]);

  useEffect(() => {
    const requestResponseDocuments = {
      requestedDocuments: [] as any[],
      responseDocuments: [] as any[],
    };
    documentTypes?.forEach((document) => {
      if (document?.requestDocument) requestResponseDocuments.requestedDocuments.push(document);
      if (document?.responseDocument) requestResponseDocuments.responseDocuments.push(document);
    });
    setDocumentRequestResponseDocuments(requestResponseDocuments);
  }, [documentTypes]);

  useEffect(() => {
    if (!isPreAuthTabActive()) return;

    const basicPrefill = getFhplPrefillFromBasicDetails(patients?.fetchBasicDetails);
    const saved = mapFhplPreAuthOthersToForm(fhplPortalData);

    form.setFieldsValue({
      ...basicPrefill,
      ...saved,
      patientName: basicPrefill.patientName ?? saved.patientName,
      policyNumber: saved.policyNumber || basicPrefill.policyNumber,
      patientMobile: saved.patientMobile || basicPrefill.patientMobile,
      treatingDoctorName: saved.treatingDoctorName || basicPrefill.treatingDoctorName,
      dateOfBirth: saved.dateOfBirth
        ? parseFhplDate(saved.dateOfBirth)
        : basicPrefill.dateOfBirth
          ? parseFhplDate(basicPrefill.dateOfBirth)
          : null,
      probableAdmissionDate: saved.probableAdmissionDate
        ? parseFhplDate(saved.probableAdmissionDate)
        : basicPrefill.probableAdmissionDate
          ? parseFhplDate(basicPrefill.probableAdmissionDate)
          : null,
      dateOfFirstConsultation: saved.dateOfFirstConsultation
        ? parseFhplDate(saved.dateOfFirstConsultation)
        : null,
      dateOfInjury: saved.dateOfInjury ? parseFhplDate(saved.dateOfInjury) : null,
      lmp: saved.lmp ? parseFhplDate(saved.lmp) : null,
      dateOfDelivery: saved.dateOfDelivery ? parseFhplDate(saved.dateOfDelivery) : null,
      ageUnit: saved.ageUnit || basicPrefill.ageUnit || "Year",
      ailmentDurationUnit: saved.ailmentDurationUnit || "Days",
      hypertension: saved.hypertension || { unit: "Days" },
      diabetesMellitus: saved.diabetesMellitus || { unit: "Days" },
      ischemicHeartDisease: saved.ischemicHeartDisease || { unit: "Days" },
      copdBronchialAsthma: saved.copdBronchialAsthma || { unit: "Days" },
      cancer: saved.cancer || { unit: "Days" },
      alcoholism: saved.alcoholism || { unit: "Days" },
      drugAbuse: saved.drugAbuse || { unit: "Days" },
      hivOrStd: saved.hivOrStd || { unit: "Days" },
      smoking: saved.smoking || { unit: "Days" },
      tobaccoChewing: saved.tobaccoChewing || { unit: "Days" },
      ghutkaChewing: saved.ghutkaChewing || { unit: "Days" },
      hyperlipidemias: saved.hyperlipidemias || { unit: "Days" },
      osteoarthritis: saved.osteoarthritis || { unit: "Days" },
    });
  }, [fhplPortalData, patients?.fetchBasicDetails, form, activeKey, preAuth?.stepperData]);

  const getTimelineDetail = async () => {
    try {
      const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
      const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
      if (!caseId || !visitId) return;

      await dispatch(getTimelineDetails({ caseId, visitId }));
    } catch (err: any) {
      errorMessage(err?.message || "Failed to fetch timeline");
    }
  };

  const validateRequiredDocuments = (
    mandatoryDocuments: any,
    documentUrlsList: any
  ) => {
    for (const doc of mandatoryDocuments || []) {
      if (doc.required) {
        const isUploaded = documentUrlsList?.some(
          (uploadedDoc: any) => uploadedDoc.documentLabelType === doc.label
        );
        if (!isUploaded) {
          return false;
        }
      }
    }
    return true;
  };

  const getBasePayload = async (action: string = "save") => {
    const values = form.getFieldsValue(true);
    const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
    const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
    const hospitalId = patients?.fetchBasicDetails?.casesResponse?.healthCareId;
    const schemeMetaDataId = patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId;
    const portalMetaDataId = patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0];
    const existingCasePreAuthId = fhplPortalData?.casePreAuthResponse?.id || 0;

    const formattedValues = {
      ...values,
      dateOfBirth: formatFhplDate(values?.dateOfBirth),
      probableAdmissionDate: formatFhplDate(values?.probableAdmissionDate),
      dateOfFirstConsultation: formatFhplDate(values?.dateOfFirstConsultation),
      dateOfInjury: formatFhplDate(values?.dateOfInjury),
      lmp: formatFhplDate(values?.lmp),
      dateOfDelivery: formatFhplDate(values?.dateOfDelivery),
      documentsList: documentUrls,
    };

    const payload = buildFhplPreAuthPayload({
      schemeMetaDataId,
      portalMetaDataId,
      casePreAuthId: existingCasePreAuthId,
      hospitalId,
      schemeId: schemeMetaDataId,
      beneficiaryId:
        preAuth?.portalData?.beneficiaryId ||
        patients?.fetchBasicDetails?.casesResponse?.memberId,
      patientOpdIpdNumber: patients?.fetchBasicDetails?.visitInfoResponse?.opNumber,
      integratedWithPortal: patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal,
      action,
      formValues: formattedValues,
    });

    return { payload, caseId, visitId, existingCasePreAuthId, portalMetaDataId };
  };

  const refreshAll = async () => {
    try {
      await fetchPreAuthData();
      await fetchDocuments();
      fetchStepperForStatus();
      await getTimelineDetail();
      const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
      if (visitId) await dispatch(getBasicDetails({ id: visitId }));
    } catch (err: any) {
      errorMessage(err?.message || "Failed to refresh stage data");
    }
  };

  const onSave = async () => {
    try {
      setIsSaving(true);
      const { payload, caseId, visitId } = await getBasePayload("save");
      if (!caseId || !visitId) return;
      await saveDraftFhpl(caseId, visitId, payload);
      successMessage("PreAuth saved successfully");
      await refreshAll();
    } catch (e: any) {
      errorMessage(e?.message || "Failed to save PreAuth");
    } finally {
      setIsSaving(false);
    }
  };

  const onUpdate = async () => {
    try {
      setIsSaving(true);
      const { payload, caseId, visitId, existingCasePreAuthId } = await getBasePayload("save");
      if (!caseId || !visitId || !existingCasePreAuthId) return;
      await updateDraftFhpl(caseId, visitId, existingCasePreAuthId, payload);
      successMessage("PreAuth updated successfully");
      await refreshAll();
    } catch (e: any) {
      errorMessage(e?.message || "Failed to update PreAuth");
    } finally {
      setIsSaving(false);
    }
  };

  const integratedWithPortal =
    patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal ?? true;

  const onInitiate = async () => {
    try {
      await form.validateFields();
      if (
        !validateRequiredDocuments(
          documentRequestResponseDocuments?.requestedDocuments,
          documentUrls
        )
      ) {
        errorMessage("Please, Upload all required documents");
        return;
      }
      setIsSubmitting(true);
      const { payload, caseId, visitId } = await getBasePayload("save");
      if (!caseId || !visitId) return;

      const initiatePayload = {
        ...payload,
        casePreAuthId: fhplPortalData?.casePreAuthResponse?.id || 0,
        integratedWithPortal: true,
      };

      await initiateCasePreAuthFhpl(caseId, visitId, initiatePayload);
      successMessage("PreAuth initiated on FHPL portal successfully");
      await refreshAll();
    } catch (e: any) {
      if (e?.errorFields) {
        errorMessage(MANDATORY_FIELDS_ERROR);
      } else {
        errorMessage(e?.message || "Failed to initiate PreAuth on portal");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmitManually = async (values: { manualId: string }) => {
    try {
      await form.validateFields();
      if (
        !validateRequiredDocuments(
          documentRequestResponseDocuments?.requestedDocuments,
          documentUrls
        )
      ) {
        errorMessage("Please, Upload all required documents");
        return;
      }
      setIsSubmitting(true);
      const { payload, caseId, visitId } = await getBasePayload("save");
      if (!caseId || !visitId) return;

      const manualPayload = {
        ...payload,
        casePreAuthId: fhplPortalData?.casePreAuthResponse?.id || 0,
        integratedWithPortal: false,
        casePreAuthOthersRequest: {
          ...payload.casePreAuthOthersRequest,
          claimId: values?.manualId,
        },
      };

      await initiateCasePreAuthFhpl(caseId, visitId, manualPayload);
      successMessage("PreAuth submitted manually successfully");
      setManuallyClaimIdPortal(false);
      setConfirmationModal(false);
      setConfirmationState("");
      await refreshAll();
    } catch (e: any) {
      if (e?.errorFields) {
        errorMessage(MANDATORY_FIELDS_ERROR);
      } else {
        errorMessage(e?.message || "Failed to submit PreAuth manually");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      if (
        !validateRequiredDocuments(
          documentRequestResponseDocuments?.requestedDocuments,
          documentUrls
        )
      ) {
        errorMessage("Please, Upload all required documents");
        return;
      }
      if (integratedWithPortal) {
        handleSaveConfirmationModal("initiate");
      } else {
        handleSaveConfirmationModal("submit");
      }
    } catch (err: any) {
      if (err?.errorFields) {
        errorMessage(MANDATORY_FIELDS_ERROR);
      } else {
        errorMessage(err?.message || "Please try again later");
      }
    }
  };

  const onIntimate = async () => {
    try {
      if (
        !validateRequiredDocuments(
          documentRequestResponseDocuments?.requestedDocuments,
          documentUrls
        )
      ) {
        errorMessage("Please, Upload all required documents");
        return;
      }
      setIsIntimating(true);
      const { payload, caseId, visitId } = await getBasePayload("intimate");
      if (!caseId || !visitId) return;
      await intimateFhplPreAuth(caseId, visitId, payload);
      successMessage("PreAuth sent to QC successfully");
      await refreshAll();
    } catch (e: any) {
      errorMessage(e?.message || "Failed to intimate PreAuth");
    } finally {
      setIsIntimating(false);
    }
  };

  const onApprove = async () => {
    try {
      await form.validateFields();
      if (
        !validateRequiredDocuments(
          documentRequestResponseDocuments?.requestedDocuments,
          documentUrls
        )
      ) {
        errorMessage("Please, Upload all required documents");
        return;
      }
      setIsApproving(true);
      const { payload, caseId, visitId, existingCasePreAuthId, portalMetaDataId } =
        await getBasePayload("save");
      if (!caseId || !visitId) return;

      const approvalPayload = buildFhplApprovePayload({
        caseId,
        visitId,
        stage: "PreAuth",
        approved: true,
        casePreAuthId: existingCasePreAuthId || 0,
        portalMetaDataId,
        preAuthRequest: payload,
      });

      await approveFhplPreAuth(approvalPayload);
      successMessage("PreAuth Approved Successfully");
      await refreshAll();
    } catch (e: any) {
      if (e?.errorFields) {
        errorMessage(MANDATORY_FIELDS_ERROR);
      } else {
        errorMessage(e?.message || "Failed to approve PreAuth");
      }
    } finally {
      setIsApproving(false);
    }
  };

  const handleSaveConfirmationModal = (
    nextState: "save" | "update" | "intimate" | "approve" | "initiate" | "submit"
  ) => {
    if (
      nextState === "intimate" ||
      nextState === "initiate" ||
      nextState === "submit"
    ) {
      if (
        !validateRequiredDocuments(
          documentRequestResponseDocuments?.requestedDocuments,
          documentUrls
        )
      ) {
        errorMessage("Please, Upload all required documents");
        return;
      }
    }
    setConfirmationState(nextState);
    setConfirmationModal(true);
    if (nextState === "save") {
      setConfirmationTitle("Do You want to save PreAuth ?");
    } else if (nextState === "update") {
      setConfirmationTitle("Do You want to update PreAuth ?");
    } else if (nextState === "intimate") {
      setConfirmationTitle("Do you want to send PreAuth for QC ?");
    } else if (nextState === "approve") {
      setConfirmationTitle("Do You want to approve PreAuth ?");
    } else if (nextState === "initiate") {
      setConfirmationTitle("Do you want to submit PreAuth on portal ?");
    } else if (nextState === "submit") {
      setConfirmationTitle("Do you want to submit PreAuth ?");
    }
  };

  const handleConfirmAction = async () => {
    if (confirmationState === "save") {
      await onSave();
      setConfirmationModal(false);
      setConfirmationState("");
    } else if (confirmationState === "update") {
      await onUpdate();
      setConfirmationModal(false);
      setConfirmationState("");
    } else if (confirmationState === "intimate") {
      await onIntimate();
      setConfirmationModal(false);
      setConfirmationState("");
    } else if (confirmationState === "approve") {
      await onApprove();
      setConfirmationModal(false);
      setConfirmationState("");
    } else if (confirmationState === "initiate") {
      await onInitiate();
      setConfirmationModal(false);
      setConfirmationState("");
    } else if (confirmationState === "submit") {
      setConfirmationModal(false);
      setConfirmationState("");
      setManuallyClaimIdPortal(true);
    }
  };

  const handleFileChange = (documentType: string, isMultiple: boolean) => (e: any) => {
    if (isMultiple) {
      const files = e?.fileList || [];
      setUploadFileList(files);
      if (files.length === 0) return;
      setSelectedFiles(
        files.map((f: any) => ({
          file: f.originFileObj,
          documentType,
        }))
      );
      setPreviewFiles(
        files.map((f: any) => ({
          url: URL.createObjectURL(f.originFileObj),
          name: f.name,
          showUpload: true,
          documentType,
        }))
      );
      setPreviewModalVisible(true);
    } else if (e?.file) {
      setSelectedFile({ file: e.file, documentType });
      setPreviewFile({
        url: URL.createObjectURL(e.file),
        name: e.file.name,
        showUpload: true,
        documentType,
      });
      setPreviewModalVisible(true);
    }
  };

  const handleAttachDocuments = async (uploadedDocs: any[]) => {
    try {
      const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
      const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
      if (!caseId || !visitId) return;
      await updateAllDocumentsData(caseId, visitId, {
        casePreAuthAttachments: uploadedDocs,
        caseEnhancementsAttachments: [],
        caseDischargeAttachments: [],
        caseClaimAttachments: [],
      });
      await fetchDocuments();
    } catch (err: any) {
      errorMessage(err?.message || "Failed to attach documents");
      throw err;
    }
  };

  const handleDocumentUpload = async () => {
    try {
      const formData = new FormData();
      const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
      const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
      const patientId = patients?.fetchBasicDetails?.patientResponse?.id;
      const hospitalId = patients?.fetchBasicDetails?.casesResponse?.healthCareId;
      if (hospitalId) formData.append("hospitalId", String(hospitalId));
      if (patientId) formData.append("patientId", String(patientId));
      if (caseId) formData.append("caseId", String(caseId));
      if (visitId) formData.append("visitId", String(visitId));

      if (selectedFiles.length > 0) {
        formData.append("documentType", previewFiles?.[0]?.documentType);
        selectedFiles.forEach((fileObj, index) => formData.append(`file${index}`, fileObj.file));
      } else if (selectedFile?.file) {
        formData.append("documentType", previewFile.documentType);
        formData.append("file", selectedFile.file);
      }

      const uploaded = await dispatch(
        UploadDocument({ formData, accessToken: auth?.accessToken || "" })
      ).unwrap();
      setDocumentUrls((prev) => [...prev, ...uploaded]);
      await handleAttachDocuments(uploaded);
      message.success("Document uploaded successfully");
      setPreviewModalVisible(false);
      setSelectedFile(null);
      setSelectedFiles([]);
      setUploadFileList([]);
      setPreviewFiles([]);
    } catch (err: any) {
      errorMessage(err?.message || "Failed to upload document");
    }
  };

  const handleDeleteList = async (_documentType: string, documentUrl: string) => {
    try {
      const deleteDocument = documentUrls?.find((doc: any) => doc?.documentUrl === documentUrl);
      setDocumentUrls((prev) => prev.filter((doc: any) => doc?.documentUrl !== documentUrl));
      if (deleteDocument?.documentUrl) {
        await deleteUploadedDocument({
          documentUrl: deleteDocument?.documentUrl,
          status: false,
          id: deleteDocument?.id,
        });
      }
    } catch (err: any) {
      errorMessage(err?.message || "Failed to delete document");
    }
  };

  const fetchDeficiencyData = async (_selectedDocument: any, selectedDocumentId: any) => {
    try {
      if (!selectedDocumentId) return;
      setFetchingDeficiencyData(true);
      await dispatch(getDeficiencyQuestionData(selectedDocumentId));
    } catch (err: any) {
      errorMessage(err?.message || "Failed to fetch deficiency data");
    } finally {
      setFetchingDeficiencyData(false);
    }
  };

  const handleDeficiencyModalOpen = async (_documentUrl: string, _documentId: number, document: any) => {
    try {
      await fetchDeficiencyData(document, document?.id);
      setSelectedDocument(document);
    } catch (err: any) {
      errorMessage(err?.message || "Failed to open deficiency modal");
    }
  };

  const closeDeficiencyModal = () => {
    setDeficiencyModal(false);
    setSelectedDocument(null);
  };

  const handleOpen = (docType: any, documentUrl: any) => {
    setPreviewDocument(true);
    setClickedActiveTab(docType);
    setClickedActiveDocumentUrl(documentUrl);
  };

  const isSubmitted =
    Boolean(fhplPortalData?.claimId) ||
    Boolean(fhplPortalData?.casePreAuthResponse?.submitted);
  const isInitiated = Boolean(fhplPortalData?.casePreAuthResponse?.initiated);
  const isIntimated = Boolean(fhplPortalData?.casePreAuthResponse?.intimated);
  const hasExistingRecord = Boolean(
    fhplPortalData?.casePreAuthResponse?.id || fhplPortalData?.id
  );
  const isApproved = Boolean(fhplPortalData?.casePreAuthResponse?.approved);
  const showVerifyButton = Boolean(fhplPortalData?.claimId);

  const handleClaimValidation = async () => {
    await handlePortalClaimValidationFlow({
      scheme: "FHPL",
      caseId: patients?.fetchBasicDetails?.casesResponse?.id,
      visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
      healthcareId: patients?.fetchBasicDetails?.casesResponse?.healthCareId,
      schemeId: patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
      claimId: fhplPortalData?.claimId,
      portalData: preAuth?.portalData,
      patients,
      setIsValidationLoading,
      setValidationRes,
      setRegistrationValidationStatus,
      setIsValidationModalOpen,
    });
  };

  const handleUpdateClaimValidation = async (data: any) => {
    try {
      await updatePortalClaimValidation(
        "FHPL",
        patients?.fetchBasicDetails?.casesResponse?.id,
        patients?.fetchBasicDetails?.visitInfoResponse?.id,
        data,
      );
      successMessage("Details Updated Successfully");
      if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
        await dispatch(
          getBasicDetails({
            id: patients?.fetchBasicDetails?.visitInfoResponse?.id,
          }),
        );
      }
      await fetchPreAuthData();
    } catch (err) {
      errorMessage("Please, Try again later");
    }
  };

  return (
    <div>
      <PreAuthFhpl
        form={form}
        handleSaveConfirmationModal={handleSaveConfirmationModal}
        handleSubmit={handleSubmit}
        isSaving={isSaving}
        isIntimating={isIntimating}
        isApproving={isApproving}
        isSubmitting={isSubmitting}
        integratedWithPortal={integratedWithPortal}
        isApproved={isApproved}
        isSubmitted={isSubmitted}
        isInitiated={isInitiated}
        isIntimated={isIntimated}
        hasExistingRecord={hasExistingRecord}
        documentRequestResponseDocuments={documentRequestResponseDocuments}
        documentUrls={documentUrls}
        handleFileChange={handleFileChange}
        handleOpen={handleOpen}
        uploadFileList={uploadFileList}
        showVerifyButton={showVerifyButton}
        isValidationLoading={isValidationLoading}
        registrationValidationStatus={registrationValidationStatus}
        handleClaimValidation={handleClaimValidation}
      />

      {selectedFile?.file && !selectedFiles?.length && (
        <FilePreviewModal
          isVisible={previewModalVisible}
          onClose={() => setPreviewModalVisible(false)}
          fileUrl={previewFile.url}
          fileName={previewFile.name}
          documentType={previewFile.documentType}
          handleUpload={handleDocumentUpload}
          showUploadButton={previewFile.showUpload}
          key={selectedFile?.file?.lastModified || "no-file"}
          isLoading={enhancement?.isLoading}
        />
      )}

      {selectedFiles?.length > 0 && (
        <MultipleFilesPreviewModal
          isVisible={previewModalVisible}
          onClose={() => {
            setPreviewModalVisible(false);
            setSelectedFiles([]);
            setUploadFileList([]);
          }}
          files={previewFiles}
          handleUpload={handleDocumentUpload}
          showUploadButton={previewFiles?.some((f) => f.showUpload)}
          key={previewFiles?.map((f) => f.name).join(",") || "no-file"}
          isLoading={enhancement?.isLoading}
        />
      )}

      <PatientDetailsStageDocument
        handleCancel={() => setPreviewDocument(false)}
        handleDeleteDocument={handleDeleteList}
        open={previewDocument}
        handleDownloadDocument={async () => {}}
        claimId={fhplPortalData?.claimId}
        documents={documentUrls}
        handleDeficiencyModal={handleDeficiencyModalOpen}
        closeDeficiencyModal={closeDeficiencyModal}
        deficiencyModal={deficiencyModal}
        deficiencyQuestions={patients?.deficiencyCheckQuestions}
        setDocumentName={setDocumentName}
        selectedDocument={selectedDocument}
        setUploadedDocumentUrl={setDocumentUrls}
        activeKey={activeKey}
        clickedActiveTab={clickedActiveTab}
        clickedActiveDocumentUrl={clickedActiveDocumentUrl}
        isSubmittedStatus={isSubmitted}
        isIntimated={isIntimated}
        fetchingDeficiencyData={fetchingDeficiencyData}
      />

      <DocumentDeficiencyModal
        closeDeficiencyModal={closeDeficiencyModal}
        deficiencyModal={deficiencyModal}
        documentUrl={clickedActiveDocumentUrl || ""}
        documentName={documentName}
        handleDownloadDocument={async () => {}}
        deficiencyQuestions={patients?.deficiencyCheckQuestions}
        selectedDocument={selectedDocument}
        setUploadedDocumentUrl={setDocumentUrls}
      />

      <ConfirmSubmissionModalStage
        open={confirmationModal}
        title={confirmationTitle}
        handleCancel={() => setConfirmationModal(false)}
        handleSave={handleConfirmAction}
        isLoading={isSaving || isIntimating || isApproving || isSubmitting}
      />

      <ManuallyClaimIdPortal
        open={manuallyClaimIdPortal}
        title="Enter Claim Id"
        handleCancel={() => setManuallyClaimIdPortal(false)}
        handleSave={onSubmitManually}
        footer="Save"
        isLoading={isSubmitting}
      />

      <PortalClaimValidationModal
        open={isValidationModalOpen}
        onCancel={() => setIsValidationModalOpen(false)}
        validationRes={validationRes}
        scheme="FHPL"
        onUpdate={handleUpdateClaimValidation}
      />
    </div>
  );
};

export default React.memo(PreAuthFhplContainer);
